/**
 * Batch Inventory Dashboard — Module 9: Fresh/Expiry Management
 * Batch-level inventory tracking with expiry dates, FEFO allocation, write-off alerts.
 * Tabs: Expiring Soon | All Batches | Write-Offs | FEFO Allocations
 */
import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Tab,
  Tabs,
  IconButton,
  Tooltip,
  LinearProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Warning as WarningIcon,
  Delete as WriteOffIcon,
  Refresh as RefreshIcon,
  Add as AddIcon,
  LocalOffer as MarkdownIcon,
  Inventory as BatchIcon,
  Schedule as ExpiryIcon,
  Assessment as SummaryIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../../services/api';
import UniversalFilters from '../common/UniversalFilters';
import ExportMenu from '../Common/ExportMenu';
import { useCurrencyFormat } from '../../hooks/useCurrencyFormat';
import { useFullscreen, FullscreenButton } from '../Common/FullscreenButton';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface BatchRecord {
  batch_id: string;
  store_id: string;
  store_name?: string;
  product_id: string;
  product_name?: string;
  sku?: string;
  category?: string;
  batch_number: string | null;
  lot_number: string | null;
  manufacturing_date: string | null;
  expiry_date: string;
  received_quantity: number;
  current_quantity: number;
  reserved_quantity: number;
  unit_cost: number | null;
  total_value: number | null;
  days_to_expiry: number | null;
  shelf_life_remaining_pct: number | null;
  status: string;
  markdown_applied: boolean;
  markdown_pct: number | null;
  write_off_date: string | null;
  write_off_quantity: number | null;
  write_off_value: number | null;
  received_at: string;
}

interface WriteOffSummary {
  total_writeoff_mad: number;
  by_category: Array<{ category: string; total: number; count: number }>;
  by_month: Array<{ month: string; total: number; count: number }>;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const statusColor = (status: string): 'success' | 'warning' | 'error' | 'default' | 'info' => {
  switch (status) {
    case 'active': return 'success';
    case 'expiring_soon': return 'warning';
    case 'expired': return 'error';
    case 'written_off': return 'default';
    case 'depleted': return 'info';
    default: return 'default';
  }
};

const formatDate = (d: string | null) =>
  d ? new Date(d).toLocaleDateString() : '-';

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const BatchInventoryDashboard: React.FC = () => {
  const { t } = useTranslation();
  const { fmtCurrencyPrecise: formatCurrency, fmtCurrencyCompact, fmtCurrencyLabel } = useCurrencyFormat();
  const queryClient = useQueryClient();
  const { isFullscreen, toggle: toggleFullscreen, FullscreenWrap } = useFullscreen();
  const [activeTab, setActiveTab] = useState(0);

  // Universal filters state
  const [filters, setFilters] = useState<{
    brands: string[];
    stores: string[];
    categories: string[];
    cities: string[];
    abcClassifications: string[];
    start_date: string;
    end_date: string;
  }>({
    brands: [],
    stores: [],
    categories: [],
    cities: [],
    abcClassifications: [],
    start_date: new Date(Date.now() - 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    end_date: new Date().toISOString().split('T')[0],
  });

  // Build filter params for API calls
  const getFilterParams = () => ({
    brands: filters.brands.length > 0 ? filters.brands.join(',') : undefined,
    stores: filters.stores.length > 0 ? filters.stores.join(',') : undefined,
    categories: filters.categories.length > 0 ? filters.categories.join(',') : undefined,
    cities: filters.cities.length > 0 ? filters.cities.join(',') : undefined,
    abc_classifications: filters.abcClassifications.length > 0 ? filters.abcClassifications.join(',') : undefined,
    start_date: filters.start_date || undefined,
    end_date: filters.end_date || undefined,
  });

  // Dialogs
  const [addBatchOpen, setAddBatchOpen] = useState(false);
  const [writeOffOpen, setWriteOffOpen] = useState(false);
  const [markdownOpen, setMarkdownOpen] = useState(false);
  const [fefoOpen, setFefoOpen] = useState(false);
  const [selectedBatch, setSelectedBatch] = useState<BatchRecord | null>(null);

  // Form states
  const [newBatch, setNewBatch] = useState({
    store_id: '', product_id: '', batch_number: '', lot_number: '',
    expiry_date: '', received_quantity: 0, unit_cost: 0,
  });
  const [writeOffQty, setWriteOffQty] = useState(0);
  const [markdownPct, setMarkdownPct] = useState(0);
  const [fefoData, setFefoData] = useState({ product_id: '', store_id: '', quantity_needed: 0 });
  const [fefoResult, setFefoResult] = useState<any[]>([]);
  const [expiryThreshold, setExpiryThreshold] = useState(30);

  // ---------- Queries ----------

  const { data: expiringSoon, isLoading: loadingExpiring } = useQuery<BatchRecord[]>({
    queryKey: ['batchExpiringSoon', expiryThreshold, filters],
    queryFn: async () => {
      const res = await api.getExpiringSoon({ days_threshold: expiryThreshold, ...getFilterParams() });
      return res.data;
    },
  });

  const { data: allBatches, isLoading: loadingAll } = useQuery<BatchRecord[]>({
    queryKey: ['allBatches', filters],
    queryFn: async () => {
      const res = await api.getBatches(getFilterParams());
      return res.data;
    },
  });

  const { data: expiredBatches } = useQuery<BatchRecord[]>({
    queryKey: ['expiredBatches', filters],
    queryFn: async () => {
      const res = await api.getExpiredBatches(getFilterParams());
      return res.data;
    },
  });

  const { data: writeOffSummary } = useQuery<WriteOffSummary>({
    queryKey: ['writeOffSummary', filters],
    queryFn: async () => {
      const res = await api.getWriteOffSummary({ period_months: 6, ...getFilterParams() });
      return res.data;
    },
  });

  // ---------- Mutations ----------

  const receiveBatchMutation = useMutation({
    mutationFn: (data: any) => api.receiveBatch(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['allBatches'] });
      queryClient.invalidateQueries({ queryKey: ['batchExpiringSoon'] });
      setAddBatchOpen(false);
      setNewBatch({ store_id: '', product_id: '', batch_number: '', lot_number: '', expiry_date: '', received_quantity: 0, unit_cost: 0 });
    },
  });

  const writeOffMutation = useMutation({
    mutationFn: ({ batchId, quantity }: { batchId: string; quantity: number }) =>
      api.writeOffBatch(batchId, { quantity }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['allBatches'] });
      queryClient.invalidateQueries({ queryKey: ['batchExpiringSoon'] });
      queryClient.invalidateQueries({ queryKey: ['writeOffSummary'] });
      setWriteOffOpen(false);
      setSelectedBatch(null);
    },
  });

  const markdownMutation = useMutation({
    mutationFn: ({ batchId, pct }: { batchId: string; pct: number }) =>
      api.applyMarkdown(batchId, pct),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['allBatches'] });
      queryClient.invalidateQueries({ queryKey: ['batchExpiringSoon'] });
      setMarkdownOpen(false);
      setSelectedBatch(null);
    },
  });

  const updateMetricsMutation = useMutation({
    mutationFn: () => api.updateExpiryMetrics(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['allBatches'] });
      queryClient.invalidateQueries({ queryKey: ['batchExpiringSoon'] });
      queryClient.invalidateQueries({ queryKey: ['expiredBatches'] });
    },
  });

  const fefoAllocateMutation = useMutation({
    mutationFn: (data: any) => api.allocateFefo(data),
    onSuccess: (res: any) => {
      setFefoResult(res.data || []);
    },
  });

  // ---------- Computed stats ----------

  const stats = {
    totalBatches: allBatches?.length || 0,
    expiringSoon: expiringSoon?.length || 0,
    expired: expiredBatches?.length || 0,
    totalWriteOff: writeOffSummary?.total_writeoff_mad || 0,
  };

  // ---------- Export rows for <ExportMenu> ----------

  const expiringSoonExportRows = useMemo(
    () =>
      (expiringSoon || []).map((b) => ({
        Product: b.product_name || b.product_id,
        SKU: b.sku || '',
        Store: b.store_name || b.store_id,
        'Batch #': b.batch_number || '',
        'Expiry Date': formatDate(b.expiry_date),
        'Days to Expiry': b.days_to_expiry,
        'Current Qty': b.current_quantity,
        [`Value (${fmtCurrencyLabel()})`]: b.total_value?.toFixed(2) || '',
        Status: b.status,
      })),
    [expiringSoon, fmtCurrencyLabel],
  );

  const allBatchesExportRows = useMemo(
    () =>
      (allBatches || []).map((b) => ({
        Product: b.product_name || b.product_id,
        SKU: b.sku || '',
        Store: b.store_name || b.store_id,
        'Batch #': b.batch_number || '',
        'Lot #': b.lot_number || '',
        'Mfg Date': formatDate(b.manufacturing_date),
        'Expiry Date': formatDate(b.expiry_date),
        'Received Qty': b.received_quantity,
        'Current Qty': b.current_quantity,
        'Days to Expiry': b.days_to_expiry,
        'Shelf Life %': b.shelf_life_remaining_pct?.toFixed(0),
        [`Value (${fmtCurrencyLabel()})`]: b.total_value?.toFixed(2) || '',
        Status: b.status,
        Markdown: b.markdown_applied ? `${b.markdown_pct}%` : 'No',
      })),
    [allBatches, fmtCurrencyLabel],
  );

  // ---------- Render helpers ----------

  const renderBatchTable = (batches: BatchRecord[] | undefined, loading: boolean, showActions: boolean) => (
    <TableContainer component={Paper} sx={{ mt: 2 }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>{t('batch.product')}</TableCell>
            <TableCell>{t('batch.store')}</TableCell>
            <TableCell>Item Status</TableCell>
            <TableCell>{t('batch.batchNumber')}</TableCell>
            <TableCell>{t('batch.expiryDate')}</TableCell>
            <TableCell align="right">{t('batch.daysToExpiry')}</TableCell>
            <TableCell align="right">{t('batch.shelfLife')}</TableCell>
            <TableCell align="right">{t('batch.currentQty')}</TableCell>
            <TableCell align="right">{t('batch.value')}</TableCell>
            <TableCell>{t('batch.status')}</TableCell>
            {showActions && <TableCell>{t('common.actions')}</TableCell>}
          </TableRow>
        </TableHead>
        <TableBody>
          {loading ? (
            <TableRow>
              <TableCell colSpan={showActions ? 11 : 10} align="center">
                <CircularProgress size={24} />
              </TableCell>
            </TableRow>
          ) : batches && batches.length > 0 ? (
            batches.map((batch) => (
              <TableRow key={batch.batch_id} hover>
                <TableCell>
                  {batch.product_name || batch.product_id}
                  {batch.sku && <Typography variant="caption" display="block" color="text.secondary">{batch.sku}</Typography>}
                </TableCell>
                <TableCell>{batch.store_name || batch.store_id}</TableCell>
                <TableCell sx={{ py: '6px', px: '8px' }}>
                  {(batch as any).lifecycle_stage ? (
                    <Chip
                      label={(batch as any).lifecycle_stage}
                      size="small"
                      color={
                        (batch as any).lifecycle_stage === 'NEW' ? 'info' :
                        (batch as any).lifecycle_stage === 'GROWTH' ? 'success' :
                        (batch as any).lifecycle_stage === 'DECLINING' ? 'warning' :
                        ((batch as any).lifecycle_stage === 'PHASE_OUT' || (batch as any).lifecycle_stage === 'DISCONTINUED') ? 'error' :
                        'default'
                      }
                      variant="outlined"
                      sx={{ fontSize: '10px', height: 20 }}
                    />
                  ) : (
                    <Typography sx={{ fontSize: '10px', color: 'var(--color-text-muted)' }}>—</Typography>
                  )}
                </TableCell>
                <TableCell>{batch.batch_number || batch.lot_number || '-'}</TableCell>
                <TableCell>{formatDate(batch.expiry_date)}</TableCell>
                <TableCell align="right">
                  <Typography
                    color={
                      (batch.days_to_expiry ?? 999) < 0 ? 'error' :
                      (batch.days_to_expiry ?? 999) < 15 ? 'warning.main' : 'text.primary'
                    }
                    fontWeight={(batch.days_to_expiry ?? 999) < 15 ? 'bold' : 'normal'}
                  >
                    {batch.days_to_expiry ?? '-'}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  {batch.shelf_life_remaining_pct != null ? (
                    <Box display="flex" alignItems="center" gap={1}>
                      <LinearProgress
                        variant="determinate"
                        value={Math.max(0, Math.min(100, batch.shelf_life_remaining_pct))}
                        color={batch.shelf_life_remaining_pct < 20 ? 'error' : batch.shelf_life_remaining_pct < 50 ? 'warning' : 'success'}
                        sx={{ width: 60, height: 8, borderRadius: 4 }}
                      />
                      <Typography variant="caption">{batch.shelf_life_remaining_pct.toFixed(0)}%</Typography>
                    </Box>
                  ) : '-'}
                </TableCell>
                <TableCell align="right">{batch.current_quantity}</TableCell>
                <TableCell align="right">
                  {batch.total_value != null ? formatCurrency(batch.total_value) : '-'}
                </TableCell>
                <TableCell>
                  <Chip
                    label={batch.status}
                    size="small"
                    color={statusColor(batch.status)}
                  />
                  {batch.markdown_applied && (
                    <Chip label={`-${batch.markdown_pct}%`} size="small" color="info" sx={{ ml: 0.5 }} />
                  )}
                </TableCell>
                {showActions && (
                  <TableCell>
                    <Box display="flex" gap={0.5}>
                      <Tooltip title={t('batch.applyMarkdown')}>
                        <IconButton
                          size="small"
                          onClick={() => { setSelectedBatch(batch); setMarkdownPct(batch.markdown_pct || 20); setMarkdownOpen(true); }}
                          disabled={batch.status === 'written_off' || batch.status === 'depleted'}
                        >
                          <MarkdownIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t('batch.writeOff')}>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => { setSelectedBatch(batch); setWriteOffQty(batch.current_quantity); setWriteOffOpen(true); }}
                          disabled={batch.status === 'written_off' || batch.status === 'depleted'}
                        >
                          <WriteOffIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                )}
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={showActions ? 11 : 10} align="center">
                {t('batch.noBatches')}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <FullscreenWrap>
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">{t('batch.title')}</Typography>
        <Box display="flex" gap={1} alignItems="center">
          <FullscreenButton isFullscreen={isFullscreen} onToggle={toggleFullscreen} />
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => updateMetricsMutation.mutate()}
            disabled={updateMetricsMutation.isPending}
          >
            {t('batch.updateMetrics')}
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setAddBatchOpen(true)}
          >
            {t('batch.receiveBatch')}
          </Button>
        </Box>
      </Box>

      {/* Universal Filters */}
      <UniversalFilters
        brands={filters.brands}
        stores={filters.stores}
        categories={filters.categories}
        cities={filters.cities}
        abcClassifications={filters.abcClassifications}
        onBrandsChange={(brands) => setFilters({ ...filters, brands })}
        onStoresChange={(stores) => setFilters({ ...filters, stores })}
        onCategoriesChange={(categories) => setFilters({ ...filters, categories })}
        onCitiesChange={(cities) => setFilters({ ...filters, cities })}
        onAbcClassificationsChange={(abcClassifications) => setFilters({ ...filters, abcClassifications })}
        onClearAll={() => setFilters({ brands: [], stores: [], categories: [], cities: [], abcClassifications: [], start_date: new Date(Date.now() - 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], end_date: new Date().toISOString().split('T')[0] })}
        showCountries={false}
        showRetailers={false}
        showBrands={true}
        showStores={true}
        showCategories={true}
        showCities={true}
        showAbcClassifications={true}
        customFilters={
          <>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                size="small"
                type="date"
                label="Start Date"
                value={filters.start_date}
                onChange={(e) => setFilters({ ...filters, start_date: e.target.value })}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                size="small"
                type="date"
                label="End Date"
                value={filters.end_date}
                onChange={(e) => setFilters({ ...filters, end_date: e.target.value })}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
            </Grid>
          </>
        }
      />

      {/* KPI Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1}>
                <BatchIcon color="primary" />
                <Typography variant="body2" color="text.secondary">{t('batch.totalBatches')}</Typography>
              </Box>
              <Typography variant="h4" sx={{ mt: 1 }}>{stats.totalBatches}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1}>
                <ExpiryIcon color="warning" />
                <Typography variant="body2" color="text.secondary">{t('batch.expiringSoon')}</Typography>
              </Box>
              <Typography variant="h4" color="warning.main" sx={{ mt: 1 }}>{stats.expiringSoon}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1}>
                <WarningIcon color="error" />
                <Typography variant="body2" color="text.secondary">{t('batch.expired')}</Typography>
              </Box>
              <Typography variant="h4" color="error" sx={{ mt: 1 }}>{stats.expired}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1}>
                <SummaryIcon color="info" />
                <Typography variant="body2" color="text.secondary">{t('batch.totalWriteOff')}</Typography>
              </Box>
              <Typography variant="h4" color="info.main" sx={{ mt: 1 }}>
                {formatCurrency(stats.totalWriteOff)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabs */}
      <Paper sx={{ mb: 2 }}>
        <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)}>
          <Tab label={t('batch.expiringSoon')} icon={<ExpiryIcon />} iconPosition="start" />
          <Tab label={t('batch.allBatches')} icon={<BatchIcon />} iconPosition="start" />
          <Tab label={t('batch.writeOffs')} icon={<WriteOffIcon />} iconPosition="start" />
          <Tab label={t('batch.fefoAllocation')} icon={<SummaryIcon />} iconPosition="start" />
        </Tabs>
      </Paper>

      {/* Tab 0: Expiring Soon */}
      {activeTab === 0 && (
        <Box>
          <Box display="flex" alignItems="center" gap={2} mb={2}>
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>{t('batch.expiryThreshold')}</InputLabel>
              <Select
                value={expiryThreshold}
                label={t('batch.expiryThreshold')}
                onChange={(e) => setExpiryThreshold(Number(e.target.value))}
              >
                <MenuItem value={7}>7 {t('batch.days')}</MenuItem>
                <MenuItem value={15}>15 {t('batch.days')}</MenuItem>
                <MenuItem value={30}>30 {t('batch.days')}</MenuItem>
                <MenuItem value={60}>60 {t('batch.days')}</MenuItem>
                <MenuItem value={90}>90 {t('batch.days')}</MenuItem>
              </Select>
            </FormControl>
            <ExportMenu
              data={expiringSoonExportRows}
              filename="Expiring_Soon_Batches"
              title="Expiring Soon Batches"
              sheetName="Expiring Soon"
            />
          </Box>
          {expiringSoon && expiringSoon.length > 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {expiringSoon.length} {t('batch.batchesExpiringSoon', { days: expiryThreshold })}
            </Alert>
          )}
          {renderBatchTable(expiringSoon, loadingExpiring, true)}
        </Box>
      )}

      {/* Tab 1: All Batches */}
      {activeTab === 1 && (
        <Box>
          <Box display="flex" justifyContent="flex-end" mb={2}>
            <ExportMenu
              data={allBatchesExportRows}
              filename="All_Batches"
              title="All Batches"
              sheetName="All Batches"
            />
          </Box>
          {renderBatchTable(allBatches, loadingAll, true)}
        </Box>
      )}

      {/* Tab 2: Write-Offs */}
      {activeTab === 2 && (
        <Box>
          {/* Write-off summary cards */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>{t('batch.writeOffByCategory')}</Typography>
                {writeOffSummary?.by_category && writeOffSummary.by_category.length > 0 ? (
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>{t('batch.category')}</TableCell>
                        <TableCell align="right">{t('batch.count')}</TableCell>
                        <TableCell align="right">{t('batch.totalValue')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {writeOffSummary.by_category.map((row, idx) => (
                        <TableRow key={idx}>
                          <TableCell>{row.category}</TableCell>
                          <TableCell align="right">{row.count}</TableCell>
                          <TableCell align="right">{formatCurrency(row.total)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <Typography color="text.secondary">{t('batch.noWriteOffs')}</Typography>
                )}
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>{t('batch.writeOffByMonth')}</Typography>
                {writeOffSummary?.by_month && writeOffSummary.by_month.length > 0 ? (
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>{t('batch.month')}</TableCell>
                        <TableCell align="right">{t('batch.count')}</TableCell>
                        <TableCell align="right">{t('batch.totalValue')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {writeOffSummary.by_month.map((row, idx) => (
                        <TableRow key={idx}>
                          <TableCell>{row.month}</TableCell>
                          <TableCell align="right">{row.count}</TableCell>
                          <TableCell align="right">{formatCurrency(row.total)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <Typography color="text.secondary">{t('batch.noWriteOffs')}</Typography>
                )}
              </Paper>
            </Grid>
          </Grid>

          {/* Expired batches that need write-off */}
          <Typography variant="h6" gutterBottom>{t('batch.expiredNeedingWriteOff')}</Typography>
          {renderBatchTable(expiredBatches, false, true)}
        </Box>
      )}

      {/* Tab 3: FEFO Allocation */}
      {activeTab === 3 && (
        <Box>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>{t('batch.fefoAllocation')}</Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {t('batch.fefoDescription')}
            </Typography>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label={t('batch.productId')}
                  value={fefoData.product_id}
                  onChange={(e) => setFefoData({ ...fefoData, product_id: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label={t('batch.storeId')}
                  value={fefoData.store_id}
                  onChange={(e) => setFefoData({ ...fefoData, store_id: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <TextField
                  fullWidth
                  type="number"
                  label={t('batch.quantityNeeded')}
                  value={fefoData.quantity_needed}
                  onChange={(e) => setFefoData({ ...fefoData, quantity_needed: parseInt(e.target.value) || 0 })}
                  inputProps={{ min: 1 }}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <Button
                  variant="contained"
                  fullWidth
                  sx={{ height: '100%' }}
                  onClick={() => fefoAllocateMutation.mutate(fefoData)}
                  disabled={fefoAllocateMutation.isPending || !fefoData.product_id || !fefoData.store_id || fefoData.quantity_needed < 1}
                >
                  {t('batch.allocate')}
                </Button>
              </Grid>
            </Grid>
          </Paper>

          {fefoResult.length > 0 && (
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>{t('batch.allocationResult')}</Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t('batch.batchId')}</TableCell>
                    <TableCell>{t('batch.batchNumber')}</TableCell>
                    <TableCell>{t('batch.expiryDate')}</TableCell>
                    <TableCell align="right">{t('batch.allocatedQty')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {fefoResult.map((alloc: any, idx: number) => (
                    <TableRow key={idx}>
                      <TableCell>{alloc.batch_id}</TableCell>
                      <TableCell>{alloc.batch_number || '-'}</TableCell>
                      <TableCell>{formatDate(alloc.expiry_date)}</TableCell>
                      <TableCell align="right">{alloc.allocated_qty}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>
          )}
        </Box>
      )}

      {/* ===================== DIALOGS ===================== */}

      {/* Add Batch Dialog */}
      <Dialog open={addBatchOpen} onClose={() => setAddBatchOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('batch.receiveBatch')}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth margin="normal" label={t('batch.storeId')}
            value={newBatch.store_id}
            onChange={(e) => setNewBatch({ ...newBatch, store_id: e.target.value })}
          />
          <TextField
            fullWidth margin="normal" label={t('batch.productId')}
            value={newBatch.product_id}
            onChange={(e) => setNewBatch({ ...newBatch, product_id: e.target.value })}
          />
          <TextField
            fullWidth margin="normal" label={t('batch.batchNumber')}
            value={newBatch.batch_number}
            onChange={(e) => setNewBatch({ ...newBatch, batch_number: e.target.value })}
          />
          <TextField
            fullWidth margin="normal" label={t('batch.lotNumber')}
            value={newBatch.lot_number}
            onChange={(e) => setNewBatch({ ...newBatch, lot_number: e.target.value })}
          />
          <TextField
            fullWidth margin="normal" label={t('batch.expiryDate')} type="date"
            value={newBatch.expiry_date}
            onChange={(e) => setNewBatch({ ...newBatch, expiry_date: e.target.value })}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            fullWidth margin="normal" label={t('batch.receivedQty')} type="number"
            value={newBatch.received_quantity}
            onChange={(e) => setNewBatch({ ...newBatch, received_quantity: parseInt(e.target.value) || 0 })}
            inputProps={{ min: 1 }}
          />
          <TextField
            fullWidth margin="normal" label={t('batch.unitCost')} type="number"
            value={newBatch.unit_cost}
            onChange={(e) => setNewBatch({ ...newBatch, unit_cost: parseFloat(e.target.value) || 0 })}
            inputProps={{ min: 0, step: 0.01 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddBatchOpen(false)}>{t('common.cancel')}</Button>
          <Button
            variant="contained"
            onClick={() => receiveBatchMutation.mutate(newBatch)}
            disabled={receiveBatchMutation.isPending || !newBatch.store_id || !newBatch.product_id || !newBatch.expiry_date}
          >
            {t('batch.receive')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Write-Off Dialog */}
      <Dialog open={writeOffOpen} onClose={() => setWriteOffOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('batch.writeOff')}</DialogTitle>
        <DialogContent>
          {selectedBatch && (
            <Box>
              <Typography variant="body2" gutterBottom>
                {t('batch.product')}: {selectedBatch.product_name || selectedBatch.product_id}
              </Typography>
              <Typography variant="body2" gutterBottom>
                {t('batch.batchNumber')}: {selectedBatch.batch_number || '-'}
              </Typography>
              <Typography variant="body2" gutterBottom>
                {t('batch.currentQty')}: {selectedBatch.current_quantity}
              </Typography>
              <TextField
                fullWidth margin="normal"
                label={t('batch.writeOffQty')}
                type="number"
                value={writeOffQty}
                onChange={(e) => setWriteOffQty(parseInt(e.target.value) || 0)}
                inputProps={{ min: 1, max: selectedBatch.current_quantity }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setWriteOffOpen(false)}>{t('common.cancel')}</Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => selectedBatch && writeOffMutation.mutate({ batchId: selectedBatch.batch_id, quantity: writeOffQty })}
            disabled={writeOffMutation.isPending || writeOffQty < 1}
          >
            {t('batch.confirmWriteOff')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Markdown Dialog */}
      <Dialog open={markdownOpen} onClose={() => setMarkdownOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('batch.applyMarkdown')}</DialogTitle>
        <DialogContent>
          {selectedBatch && (
            <Box>
              <Typography variant="body2" gutterBottom>
                {t('batch.product')}: {selectedBatch.product_name || selectedBatch.product_id}
              </Typography>
              <Typography variant="body2" gutterBottom>
                {t('batch.daysToExpiry')}: {selectedBatch.days_to_expiry}
              </Typography>
              <TextField
                fullWidth margin="normal"
                label={t('batch.markdownPct')}
                type="number"
                value={markdownPct}
                onChange={(e) => setMarkdownPct(parseFloat(e.target.value) || 0)}
                inputProps={{ min: 1, max: 100, step: 5 }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMarkdownOpen(false)}>{t('common.cancel')}</Button>
          <Button
            variant="contained"
            onClick={() => selectedBatch && markdownMutation.mutate({ batchId: selectedBatch.batch_id, pct: markdownPct })}
            disabled={markdownMutation.isPending || markdownPct < 1}
          >
            {t('batch.applyMarkdown')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
    </FullscreenWrap>
  );
};

export default BatchInventoryDashboard;
